import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { Provider as ReduxQueryProvider } from "redux-query-react";
import { RouterProvider } from "react-router-dom";
import "./index.css";
import { router } from "./router";
import reportWebVitals from "./reportWebVitals";
import store from "./store/rootReducer";
import type { RootState } from "StoreTypes";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import ReactGA from "react-ga4";
import React from "react";
import { config } from "./config/env";

export const getQueries = (state: RootState) => state.queries;
const launchDarklyClientId = config.launchDarklyClientId;
const nodeEnv = process.env.NODE_ENV;

ReactGA.initialize("G-0N3NVVZ4BM", {
  testMode: nodeEnv !== "production" ? true : false,
});

// Neither the route boundaries nor a React error boundary sees an error thrown
// from an event handler, a timer, or a floating promise, which is where a good
// share of real crashes live. Console is the whole story for now, so at least
// put them somewhere a developer can be pointed at.
window.addEventListener("error", (event) => {
  console.error("[window.onerror]", event.error ?? event.message, {
    path: window.location.pathname,
  });
});
window.addEventListener("unhandledrejection", (event) => {
  console.error("[unhandledrejection]", event.reason, {
    path: window.location.pathname,
  });
});

void (async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: launchDarklyClientId,
    reactOptions: {
      useCamelCaseFlagKeys: true,
    },
  });

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root element not found");
  }

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <Provider store={store}>
        <ReduxQueryProvider queriesSelector={getQueries}>
          {LDProvider && React.createElement(LDProvider as any, {}, <RouterProvider router={router} />)}
        </ReduxQueryProvider>
      </Provider>
    </StrictMode>,
  );
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
